(function () {
  const root = document.querySelector('[data-cart-recommendations]')
  if (!root) return

  const section = root.querySelector('[data-recommendation-block="complementary-services"]')
  const slider = section?.querySelector('.recs-complementary')
  const wrapper = slider?.querySelector('.swiper-wrapper')
  const productContextsNode = root.querySelector('[data-cart-product-contexts]')

  if (!section || !slider || !wrapper || !productContextsNode) return

  let cartProductContexts = []

  try {
    cartProductContexts = JSON.parse(productContextsNode.textContent)
      .map(({ productId, groupId, lineValue }) => ({
        productId: String(productId || ''),
        groupId: String(groupId || productId || ''),
        lineValue: Math.max(0, Number(lineValue) || 0),
      }))
      .filter(({ productId, groupId }) => productId && groupId)
  } catch (error) {
    console.error('[cart-complementary] invalid cart product contexts', error)
    return
  }

  if (!cartProductContexts.length) return

  const recommendationAnchors = [...cartProductContexts
    .reduce((groups, context) => {
      const existing = groups.get(context.groupId)
      if (existing) existing.lineValue += context.lineValue
      else groups.set(context.groupId, { ...context })
      return groups
    }, new Map())
    .values()]
    .sort((left, right) => right.lineValue - left.lineValue)
  const cartExclusionIds = new Set(
    cartProductContexts.flatMap(({ productId, groupId }) => [productId, groupId]),
  )
  const sectionId = root.dataset.sectionId
  const route = root.dataset.recommendationsRoute
  const maximumProducts = Math.min(4, Math.max(3, recommendationAnchors.length))

  function prepareCard(slide, position) {
    const card = slide.querySelector('.svc-card')
    if (!card) return

    card.dataset.productId = slide.dataset.complementaryProductId || ''
    card.dataset.recommendationBlock = 'complementary-services'
    card.dataset.recommendationSource = 'shopify-search-and-discovery'
    card.dataset.position = String(position)

    const titleLink = card.querySelector('.svc-card__title')
    if (titleLink) titleLink.dataset.recommendationAction = 'product-click'

    card.querySelectorAll('.svc-card__choose').forEach((button) => {
      button.dataset.recommendationAction = 'option-open'
    })

    card.querySelectorAll('.add_to_cart_button').forEach((button) => {
      button.dataset.recommendationAction = 'add-to-cart'
    })
  }

  function initCards() {
    if (typeof window.initRecsSwipers === 'function') {
      window.initRecsSwipers()
    }

    if (window.productCardManager) {
      window.productCardManager.initCards(slider)
    }

    if (typeof window.initRecsSimplebars === 'function') {
      window.initRecsSimplebars(slider)
    }
  }

  function deduplicateRecentlyViewed(complementaryIds) {
    const viewedSlider = root.querySelector('.recs-viewed')
    const viewedSection = viewedSlider?.closest('.product-recommendations')
    if (!viewedSlider || !viewedSection) return

    if (viewedSlider.swiper) {
      viewedSlider.swiper.destroy(true, true)
    }

    viewedSlider.querySelectorAll('.swiper-slide').forEach((slide) => {
      const productId = slide.querySelector('.svc-card')?.dataset.productId
      if (productId && complementaryIds.has(String(productId))) {
        slide.remove()
      }
    })

    const remaining = viewedSlider.querySelectorAll('.swiper-wrapper > .swiper-slide').length
    viewedSection.hidden = remaining < 2

    if (remaining >= 2 && typeof window.initRecsSwipers === 'function') {
      window.initRecsSwipers()
    }
  }

  async function fetchForProduct({ productId, groupId }, anchorOrder) {
    const url = new URL(route, window.location.origin)
    url.searchParams.set('section_id', sectionId)
    // The canonical parent owns the recommendation policy. A subproduct in the
    // cart must not need a second, independently maintained copy of that policy.
    url.searchParams.set('product_id', groupId)
    url.searchParams.set('limit', String(maximumProducts))
    url.searchParams.set('intent', 'complementary')

    const response = await fetch(url.toString(), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const documentFragment = document.createElement('div')
    documentFragment.innerHTML = await response.text()

    return [...documentFragment.querySelectorAll('[data-cart-complementary-response] > .swiper-slide')]
      .map((slide, anchorPosition) => {
        slide.dataset.complementaryAnchorId = groupId
        slide.dataset.complementaryCartProductId = productId
        return { slide, groupId, anchorOrder, anchorPosition }
      })
  }

  function selectBalancedRecommendations(ranked, perAnchor) {
    const selected = []
    const selectedIds = new Set()

    function add(candidate) {
      if (!candidate || selectedIds.has(candidate.productId) || selected.length >= maximumProducts) return
      selected.push(candidate)
      selectedIds.add(candidate.productId)
    }

    // A product that complements more than one cart group is the strongest fit.
    ;[...ranked.values()]
      .filter((candidate) => candidate.anchorMatches > 1)
      .sort((a, b) => (
        b.anchorMatches - a.anchorMatches
        || a.bestAnchorPosition - b.bestAnchorPosition
        || a.firstAnchorOrder - b.firstAnchorOrder
      ))
      .forEach(add)

    // Then round-robin the cart groups so one platform cannot consume the block.
    const maximumDepth = Math.max(0, ...perAnchor.map((candidates) => candidates.length))
    for (let depth = 0; depth < maximumDepth && selected.length < maximumProducts; depth += 1) {
      perAnchor.forEach((candidates) => add(candidates[depth]))
    }

    // Defensive fill for partial/duplicate API responses.
    ;[...ranked.values()]
      .sort((a, b) => (
        b.anchorMatches - a.anchorMatches
        || a.bestAnchorPosition - b.bestAnchorPosition
        || a.firstAnchorOrder - b.firstAnchorOrder
      ))
      .forEach(add)

    return selected
  }

  async function renderComplementaryProducts() {
    const results = await Promise.allSettled(
      recommendationAnchors.map((context, anchorOrder) => fetchForProduct(context, anchorOrder)),
    )
    const ranked = new Map()
    const perAnchor = recommendationAnchors.map(() => [])

    results.forEach((result) => {
      if (result.status !== 'fulfilled') {
        console.warn('[cart-complementary] recommendation request failed', result.reason)
        return
      }

      const seenForAnchor = new Set()

      result.value.forEach(({ slide, groupId, anchorOrder, anchorPosition }) => {
        const productId = String(slide.dataset.complementaryProductId || '')
        if (!productId || cartExclusionIds.has(productId) || seenForAnchor.has(productId)) return
        seenForAnchor.add(productId)

        const existing = ranked.get(productId)
        if (existing) {
          existing.anchorMatches += 1
          existing.anchorGroups.add(groupId)
          existing.bestAnchorPosition = Math.min(existing.bestAnchorPosition, anchorPosition)
          existing.firstAnchorOrder = Math.min(existing.firstAnchorOrder, anchorOrder)
          perAnchor[anchorOrder].push(existing)
          return
        }

        const candidate = {
          productId,
          slide,
          anchorMatches: 1,
          anchorGroups: new Set([groupId]),
          bestAnchorPosition: anchorPosition,
          firstAnchorOrder: anchorOrder,
        }
        ranked.set(productId, candidate)
        perAnchor[anchorOrder].push(candidate)
      })
    })

    const recommendations = selectBalancedRecommendations(ranked, perAnchor)

    if (!recommendations.length) return

    recommendations.forEach((recommendation, index) => {
      prepareCard(recommendation.slide, index + 1)
      wrapper.appendChild(recommendation.slide)
    })

    section.hidden = false
    initCards()
    deduplicateRecentlyViewed(new Set(recommendations.map(({ productId }) => productId)))
  }

  renderComplementaryProducts().catch((error) => {
    console.error('[cart-complementary] render failed', error)
  })
})()
